import { useState, useEffect } from 'react';

// Initialize global variables immediately when module is loaded
// This ensures they're available even before React components mount
const initializeGlobalInboxMode = () => {
  try {
    const stored = localStorage.getItem('isUsingNewInbox');
    const initialValue = stored ? JSON.parse(stored) : false;
    window.isUsingNewInbox = initialValue;
    return initialValue;
  } catch (error) {
    console.error('Error reading inbox mode from localStorage:', error);
    window.isUsingNewInbox = false;
    return false;
  }
};

// Initialize immediately when module loads
const initialGlobalValue = initializeGlobalInboxMode();

// Global function to set inbox mode (available immediately)
window.setGlobalInboxMode = (newValue) => {
  if (typeof newValue === 'boolean') {
    window.isUsingNewInbox = newValue;
    try {
      localStorage.setItem('isUsingNewInbox', JSON.stringify(newValue));
    } catch (error) {
      console.error('Error saving inbox mode to localStorage:', error);
    }
    // Dispatch event for jQuery listeners
    window.dispatchEvent(new CustomEvent('inboxModeChanged', {
      detail: { isUsingNewInbox: newValue }
    }));
  } else {
    console.warn('setGlobalInboxMode expects a boolean value');
  }
};

/*
 * GLOBAL ACCESS FOR JQUERY:
 * 
 * // Check current inbox mode
 * console.log(window.isUsingNewInbox); // true/false
 * 
 * // Change inbox mode programmatically  
 * window.setGlobalInboxMode(true);   // Switch to new inbox
 * window.setGlobalInboxMode(false);  // Switch to traditional messaging
 * 
 * // Listen for mode changes
 * $(window).on('inboxModeChanged', function(event) {
 *   const isNewInbox = event.originalEvent.detail.isUsingNewInbox;
 *   if (isNewInbox) {
 *     console.log('Switched to new inbox');
 *     // Hide traditional messaging UI
 *     $('.traditional-messaging').hide();
 *   } else {
 *     console.log('Switched to traditional messaging');
 *     // Show traditional messaging UI
 *     $('.traditional-messaging').show();
 *   }
 * });
 * 
 * // Use in conditional logic
 * $(document).ready(function() {
 *   if (window.isUsingNewInbox) {
 *     // Initialize new inbox specific behaviors
 *   } else {
 *     // Initialize traditional messaging behaviors
 *   }
 * });
 */

/**
 * Custom hook to manage global inbox mode state.
 * Sets a global variable indicating if the new inbox is being used.
 *
 * Rules:
 * - false when accessing paths containing '/messaging'
 * - true when accessing paths containing '/inbox'
 * - false as default value
 * - Accessible from jQuery scripts through window.isUsingNewInbox
 * - Persists state across page reloads
 *
 * @returns {[boolean, function]} [isUsingNewInbox, setIsUsingNewInbox]
 *
 * @example
 * const [isUsingNewInbox, setIsUsingNewInbox] = useGlobalInboxMode();
 *
 * // Also accessible from jQuery:
 * // window.isUsingNewInbox
 * // window.setGlobalInboxMode(true/false)
 */
const useGlobalInboxMode = () => {

  // Initialize state from the global value that was set on module load
  const [isUsingNewInbox, setIsUsingNewInbox] = useState(initialGlobalValue);

  // Function to check current path and update state accordingly
  const checkAndUpdateInboxMode = () => {
    const currentPath = window.location.pathname;

    if (currentPath.includes('/inbox')) {
      updateInboxMode(true);
    } else if (currentPath.includes('/messaging')) {
      updateInboxMode(false);
    }
    // For other paths, maintain current state (no action needed)
  };

  // Unified function to update both React state and global variable
  const updateInboxMode = (newValue) => {
    if (window.isUsingNewInbox !== newValue) {
      window.isUsingNewInbox = newValue;
      setIsUsingNewInbox(newValue);
      
      try {
        localStorage.setItem('isUsingNewInbox', JSON.stringify(newValue));
      } catch (error) {
        console.error('Error saving inbox mode to localStorage:', error);
      }

      // Dispatch event for jQuery listeners
      window.dispatchEvent(new CustomEvent('inboxModeChanged', {
        detail: { isUsingNewInbox: newValue }
      }));
    }
  };

  // Update state based on current path and listen for navigation changes
  useEffect(() => {
    // Check initial path and update if needed
    checkAndUpdateInboxMode();

    // Listen for popstate events (back/forward browser buttons)
    const handlePopState = () => {
      checkAndUpdateInboxMode();
    };

    // Listen for pushstate/replacestate events (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(this, args);
      setTimeout(checkAndUpdateInboxMode, 0);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      setTimeout(checkAndUpdateInboxMode, 0);
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Override the global setGlobalInboxMode function to sync with React state
  useEffect(() => {
    const originalSetGlobalInboxMode = window.setGlobalInboxMode;
    
    window.setGlobalInboxMode = (newValue) => {
      if (typeof newValue === 'boolean') {
        updateInboxMode(newValue);
      } else {
        console.warn('setGlobalInboxMode expects a boolean value');
      }
    };

    // Cleanup: restore original function on unmount
    return () => {
      window.setGlobalInboxMode = originalSetGlobalInboxMode;
    };
  }, []);

  return [isUsingNewInbox, updateInboxMode];
};

export default useGlobalInboxMode;
